import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  cancelSubscription,
  checkoutSubscription,
  confirmSubscriptionPurchase,
  getAccessibleSports,
  getMySubscription,
  getPaymentHistory,
  getSubscriptionPlans,
  reorderSports,
  resolveAccessToken,
  restoreSubscription,
  startFreeTrial,
  swapSport,
  syncSubscription,
  type CheckoutParams,
  type MySubscription,
  type SwapSportParams,
} from '@nomiss/services';
import { digestQueryKeys } from './use-digest-feed';
import { digestTimeQueryKeys } from './use-digest-time';
import { languageQueryKeys } from './use-language';
import { spoilerShieldQueryKeys } from './use-spoiler-shield';

export const subscriptionQueryKeys = {
  me: ['subscription', 'me'] as const,
  sports: ['subscription', 'sports'] as const,
  payments: ['subscription', 'payments'] as const,
  plans: ['subscription', 'plans'] as const,
};

/** Home/archive highlights are entitlement-scoped — drop cricket-only (or stale) cache. */
function invalidateDigestContentQueries(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: digestQueryKeys.all });
}

/** Digest times / languages / feed unlock from effective tier — refresh after plan changes. */
function invalidateTierGatedAccountQueries(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: digestTimeQueryKeys.me });
  void queryClient.invalidateQueries({ queryKey: languageQueryKeys.options });
  void queryClient.invalidateQueries({
    queryKey: spoilerShieldQueryKeys.preferences,
  });
  invalidateDigestContentQueries(queryClient);
}

export function useSubscriptionPlans(countryId?: string, enabled = true) {
  return useQuery({
    queryKey: [...subscriptionQueryKeys.plans, countryId ?? ''],
    queryFn: () => getSubscriptionPlans(countryId),
    enabled,
  });
}

export function useMySubscription(accessToken?: string | null, enabled = true) {
  const token = accessToken ?? resolveAccessToken();

  return useQuery({
    queryKey: [...subscriptionQueryKeys.me, token ?? ''],
    queryFn: () => getMySubscription(token),
    enabled: enabled && Boolean(token),
  });
}

export function useAccessibleSports(accessToken?: string | null, enabled = true) {
  const token = accessToken ?? resolveAccessToken();

  return useQuery({
    queryKey: [...subscriptionQueryKeys.sports, token ?? ''],
    queryFn: () => getAccessibleSports(token),
    enabled: enabled && Boolean(token),
  });
}

export function usePaymentHistory(accessToken?: string | null, enabled = true) {
  const token = accessToken ?? resolveAccessToken();

  return useQuery({
    queryKey: [...subscriptionQueryKeys.payments, token ?? ''],
    queryFn: () => getPaymentHistory(token),
    enabled: enabled && Boolean(token),
  });
}

function syncMySubscriptionCache(queryClient: QueryClient, data: MySubscription): void {
  // Queries use token-scoped keys: [...subscriptionQueryKeys.me, token].
  // Prefix setQueriesData + invalidate keeps Manage Digest + / shuffle gating fresh.
  queryClient.setQueriesData<MySubscription>({ queryKey: subscriptionQueryKeys.me }, data);
  void queryClient.invalidateQueries({ queryKey: subscriptionQueryKeys.me });
}

export function useSyncSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => syncSubscription(),
    onSuccess: (data: MySubscription) => {
      syncMySubscriptionCache(queryClient, data);
      void queryClient.invalidateQueries({ queryKey: subscriptionQueryKeys.sports });
      void queryClient.invalidateQueries({ queryKey: subscriptionQueryKeys.payments });
      invalidateTierGatedAccountQueries(queryClient);
    },
  });
}

export function useRestoreSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => restoreSubscription(),
    onSuccess: (data: MySubscription) => {
      syncMySubscriptionCache(queryClient, data);
      void queryClient.invalidateQueries({ queryKey: subscriptionQueryKeys.sports });
      void queryClient.invalidateQueries({ queryKey: subscriptionQueryKeys.payments });
      invalidateTierGatedAccountQueries(queryClient);
    },
  });
}

export function useConfirmSubscriptionPurchase() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: CheckoutParams) => confirmSubscriptionPurchase(params),
    onSuccess: result => {
      if (result.subscription) {
        syncMySubscriptionCache(queryClient, result.subscription);
        void queryClient.invalidateQueries({ queryKey: subscriptionQueryKeys.sports });
        void queryClient.invalidateQueries({ queryKey: subscriptionQueryKeys.payments });
        invalidateTierGatedAccountQueries(queryClient);
      }
    },
  });
}

export function useCheckoutSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: CheckoutParams) => checkoutSubscription(params),
    onSuccess: (data: MySubscription) => {
      syncMySubscriptionCache(queryClient, data);
      void queryClient.invalidateQueries({ queryKey: subscriptionQueryKeys.sports });
      void queryClient.invalidateQueries({ queryKey: subscriptionQueryKeys.payments });
      invalidateTierGatedAccountQueries(queryClient);
    },
  });
}

export function useStartFreeTrial() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: startFreeTrial,
    onSuccess: (data: MySubscription) => {
      syncMySubscriptionCache(queryClient, data);
      void queryClient.invalidateQueries({ queryKey: subscriptionQueryKeys.sports });
      invalidateTierGatedAccountQueries(queryClient);
    },
  });
}

export function useCancelSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: cancelSubscription,
    onSuccess: (data: MySubscription) => {
      syncMySubscriptionCache(queryClient, data);
      void queryClient.invalidateQueries({ queryKey: subscriptionQueryKeys.sports });
      void queryClient.invalidateQueries({ queryKey: subscriptionQueryKeys.payments });
      invalidateTierGatedAccountQueries(queryClient);
    },
  });
}

export function useSwapSport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: SwapSportParams) => swapSport(params),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: subscriptionQueryKeys.me });
      void queryClient.invalidateQueries({ queryKey: subscriptionQueryKeys.sports });
      invalidateDigestContentQueries(queryClient);
    },
  });
}

export function useReorderSports() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sportCodes: string[]) => reorderSports(sportCodes),
    onSuccess: async data => {
      queryClient.setQueriesData<MySubscription>(
        { queryKey: subscriptionQueryKeys.me },
        current => {
          if (!current) {
            return current;
          }

          return {
            ...current,
            entitlements: {
              ...current.entitlements,
              swapsRemaining: data.swapsRemaining,
              swapsUsed: data.swapsUsed,
              freeSwapUsed: data.freeSwapUsed,
            },
            selectedSportCodes: data.sportCodes,
          };
        },
      );

      queryClient.setQueriesData(
        { queryKey: subscriptionQueryKeys.sports },
        (
          current:
            | {
                sportCodes: string[];
                reorderSports: Array<{ code: string; name: string }>;
              }
            | undefined,
        ) => {
          if (!current) {
            return current;
          }

          const selected = new Set(data.sportCodes);
          const selectedOrdered = data.sportCodes
            .map(code => current.reorderSports.find(sport => sport.code === code))
            .filter((sport): sport is { code: string; name: string } => sport != null);
          const remainder = current.reorderSports.filter(
            sport => !selected.has(sport.code),
          );

          return {
            ...current,
            sportCodes: data.sportCodes,
            reorderSports: [...selectedOrdered, ...remainder],
          };
        },
      );

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: subscriptionQueryKeys.me }),
        queryClient.invalidateQueries({ queryKey: subscriptionQueryKeys.sports }),
        queryClient.invalidateQueries({ queryKey: digestQueryKeys.all }),
      ]);
    },
  });
}
